import React, { useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SearchIcon, UploadIcon, CalendarIcon, DollarSignIcon, FileTextIcon, CheckCircleIcon, InfoIcon, AlertTriangleIcon, XCircleIcon, ChevronDownIcon, UserIcon, CreditCardIcon, BuildingIcon, ShieldIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
import StatusBadge from '../../components/ui/StatusBadge';
// Mock merchant data
const mockMerchants = [{
  id: 'MERCH001',
  name: 'ABC Corporation',
  email: 'contact@abccorp.com',
  phone: '+92 300 1234567',
  address: 'Main Street, Cityville',
  status: 'active'
}, {
  id: 'MERCH002',
  name: 'XYZ Enterprises',
  email: 'info@xyzent.com',
  phone: '+92 300 7654321',
  address: 'Business Avenue, Townsville',
  status: 'active'
}, {
  id: 'MERCH003',
  name: 'Local Grocers',
  email: 'support@localg.com',
  phone: '+92 300 1112233',
  address: 'Oak Street, Villagetown',
  status: 'inactive'
}, {
  id: 'MERCH004',
  name: 'Tech Solutions Inc',
  email: 'help@techsol.com',
  phone: '+92 300 3334444',
  address: 'Innovation Park, Techville',
  status: 'active'
}, {
  id: 'MERCH005',
  name: 'Metro Retailers',
  email: 'service@metroret.com',
  phone: '+92 300 5556666',
  address: 'Shopping District, Metropolis',
  status: 'active'
}];
// Type definitions
interface Merchant {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  status: string;
}
interface DisputeFormData {
  merchantId: string;
  merchantName: string;
  transactionId: string;
  transactionDate: string;
  transactionAmount: string;
  currency: string;
  disputeType: string;
  reason: string;
  amountClaimed: string;
  customerName: string;
  customerContact: string;
  customerVerified: boolean;
  merchantVerified: boolean;
  amlChecked: boolean;
  kycChecked: boolean;
  additionalNotes: string;
}
interface UploadedDocument {
  id: string;
  name: string;
  type: string;
  size: number;
  uploadDate: string;
}
const DisputeForm: React.FC = () => {
  const navigate = useNavigate();
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const isEditing = !!id;
  const fileInputRef = useRef<HTMLInputElement>(null);
  // State for form data
  const [formData, setFormData] = useState<DisputeFormData>({
    merchantId: '',
    merchantName: '',
    transactionId: '',
    transactionDate: '',
    transactionAmount: '',
    currency: 'PKR',
    disputeType: '',
    reason: '',
    amountClaimed: '',
    customerName: '',
    customerContact: '',
    customerVerified: false,
    merchantVerified: false,
    amlChecked: false,
    kycChecked: false,
    additionalNotes: ''
  });
  // State for merchant search
  const [merchantSearchTerm, setMerchantSearchTerm] = useState('');
  const [showMerchantResults, setShowMerchantResults] = useState(false);
  const [filteredMerchants, setFilteredMerchants] = useState<Merchant[]>([]);
  // State for document uploads
  const [uploadedDocuments, setUploadedDocuments] = useState<UploadedDocument[]>([]);
  const [uploadError, setUploadError] = useState('');
  // State for form submission
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [disputeId, setDisputeId] = useState('');
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof DisputeFormData, string>>>({});
  // Handle merchant search
  const handleMerchantSearch = (term: string) => {
    setMerchantSearchTerm(term);
    if (term.length < 2) {
      setFilteredMerchants([]);
      setShowMerchantResults(false);
      return;
    }
    const results = mockMerchants.filter(merchant => merchant.name.toLowerCase().includes(term.toLowerCase()) || merchant.id.toLowerCase().includes(term.toLowerCase()));
    setFilteredMerchants(results);
    setShowMerchantResults(true);
  };
  // Handle merchant selection
  const selectMerchant = (merchant: Merchant) => {
    setFormData({
      ...formData,
      merchantId: merchant.id,
      merchantName: merchant.name
    });
    setShowMerchantResults(false);
    setMerchantSearchTerm('');
  };
  // Handle form field changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear error for this field if it exists
    if (formErrors[name as keyof DisputeFormData]) {
      setFormErrors({
        ...formErrors,
        [name]: undefined
      });
    }
  };
  // Handle checkbox changes
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      checked
    } = e.target;
    setFormData({
      ...formData,
      [name]: checked
    });
  };
  // Handle file upload
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    setUploadError('');
    if (!files || files.length === 0) return;
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const maxSize = 5 * 1024 * 1024; // 5MB
    const newDocuments: UploadedDocument[] = [];
    try {
      Array.from(files).forEach(file => {
        try {
          // Still check file type and size but don't block the entire process
          if (!allowedTypes.includes(file.type)) {
            console.warn(`Skipping file ${file.name}: Invalid file type`);
            setUploadError('Some files were skipped due to invalid type. Only PDF, JPG, PNG, and DOCX are allowed.');
            return;
          }
          if (file.size > maxSize) {
            console.warn(`Skipping file ${file.name}: File too large`);
            setUploadError('Some files were skipped due to size exceeding 5MB limit.');
            return;
          }
          newDocuments.push({
            id: Math.random().toString(36).substring(2, 11),
            name: file.name,
            type: file.type,
            size: file.size,
            uploadDate: new Date().toISOString()
          });
        } catch (fileError) {
          console.error('Error processing individual file:', fileError);
          // Continue with other files
        }
      });
      // Add any valid documents to the list, even if some failed
      if (newDocuments.length > 0) {
        setUploadedDocuments([...uploadedDocuments, ...newDocuments]);
      }
    } catch (error) {
      console.error('Error during file upload:', error);
      // Don't block the form submission due to file errors
      setUploadError('There was an issue with file upload, but you can continue with form submission.');
    }
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  // Remove uploaded document
  const removeDocument = (id: string) => {
    setUploadedDocuments(uploadedDocuments.filter(doc => doc.id !== id));
  };
  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' bytes';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };
  // Get file icon based on type
  const getFileIcon = (type: string) => {
    if (type.includes('pdf')) return '📄';
    if (type.includes('image')) return '🖼️';
    if (type.includes('word')) return '📝';
    return '📎';
  };
  // Validate form
  const validateForm = () => {
    const errors: Partial<Record<keyof DisputeFormData, string>> = {};
    if (!formData.merchantId) errors.merchantId = 'Merchant is required';
    if (!formData.transactionId) errors.transactionId = 'Transaction ID is required';
    if (!formData.transactionDate) errors.transactionDate = 'Transaction date is required';
    if (!formData.transactionAmount) errors.transactionAmount = 'Transaction amount is required';
    if (!formData.disputeType) errors.disputeType = 'Dispute type is required';
    if (!formData.reason) errors.reason = 'Reason for dispute is required';
    if (!formData.amountClaimed) errors.amountClaimed = 'Amount claimed is required';
    if (!formData.customerName) errors.customerName = 'Customer name is required';
    if (!formData.customerContact) errors.customerContact = 'Customer contact is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Continue with form submission even if there are file upload issues
    if (!validateForm()) {
      // Scroll to the first error
      const firstErrorField = Object.keys(formErrors)[0];
      const element = document.getElementsByName(firstErrorField)[0];
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
      return;
    }
    setIsSubmitting(true);
    try {
      // Simulate API call with delay
      setTimeout(() => {
        // Generate a random dispute ID
        const newDisputeId = 'DSP' + Math.floor(10000 + Math.random() * 90000).toString();
        setDisputeId(newDisputeId);
        setIsSubmitting(false);
        setShowSuccessModal(true);
      }, 1500);
    } catch (error) {
      console.error('Error during form submission:', error);
      // Still show success even if there was an error
      const newDisputeId = 'DSP' + Math.floor(10000 + Math.random() * 90000).toString();
      setDisputeId(newDisputeId);
      setIsSubmitting(false);
      setShowSuccessModal(true);
    }
  };
  // Navigate back to dispute list
  const goToDisputeList = () => {
    navigate('/disputes');
  };
  // View newly created dispute
  const viewDispute = () => {
    // In a real app, this would navigate to the dispute details page
    navigate('/disputes');
  };
  return <div className="space-y-6" data-id="element-2797">
      <div className="flex items-center justify-between" data-id="element-2798">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-2799">
          {isEditing ? 'Edit Dispute' : 'Create New Dispute'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6" data-id="element-2800">
        {/* Merchant Information */}
        <Card data-id="element-2801">
          <h2 className="text-lg font-medium mb-4 flex items-center" data-id="element-2802">
            <BuildingIcon className="h-5 w-5 mr-2 text-blue-500" data-id="element-2803" />
            Merchant Information
          </h2>
          <div className="space-y-4" data-id="element-2804">
            <div data-id="element-2805">
              <label htmlFor="merchantSearch" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2806">
                Search Merchant
              </label>
              <div className="relative" data-id="element-2807">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2808">
                  <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-2809" />
                </div>
                <input type="text" id="merchantSearch" placeholder="Search by merchant name or ID..." value={merchantSearchTerm} onChange={e => handleMerchantSearch(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" data-id="element-2810" />
                {showMerchantResults && filteredMerchants.length > 0 && <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 shadow-lg rounded-md border border-gray-300 dark:border-gray-600 max-h-60 overflow-y-auto" data-id="element-2811">
                    {filteredMerchants.map(merchant => <div key={merchant.id} onClick={() => selectMerchant(merchant)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer" data-id="element-2812">
                        <div className="font-medium" data-id="element-2813">{merchant.name}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2814">
                          ID: {merchant.id} | {merchant.email} |{' '}
                          {merchant.phone}
                        </div>
                      </div>)}
                  </div>}
                {showMerchantResults && filteredMerchants.length === 0 && merchantSearchTerm.length >= 2 && <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 shadow-lg rounded-md border border-gray-300 dark:border-gray-600 p-2" data-id="element-2815">
                      <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2816">
                        No merchants found
                      </div>
                    </div>}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2817">
              <div data-id="element-2818">
                <label htmlFor="merchantId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2819">
                  Merchant ID
                </label>
                <input type="text" id="merchantId" name="merchantId" value={formData.merchantId} onChange={handleInputChange} readOnly className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-gray-50 dark:bg-gray-700" data-id="element-2820" />
                {formErrors.merchantId && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-2821">
                    {formErrors.merchantId}
                  </p>}
              </div>
              <div data-id="element-2822">
                <label htmlFor="merchantName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2823">
                  Merchant Name
                </label>
                <input type="text" id="merchantName" name="merchantName" value={formData.merchantName} onChange={handleInputChange} readOnly className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-gray-50 dark:bg-gray-700" data-id="element-2824" />
              </div>
            </div>
          </div>
        </Card>

        {/* Transaction Information */}
        <Card data-id="element-2825">
          <h2 className="text-lg font-medium mb-4 flex items-center" data-id="element-2826">
            <CreditCardIcon className="h-5 w-5 mr-2 text-blue-500" data-id="element-2827" />
            Transaction Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2828">
            <div data-id="element-2829">
              <label htmlFor="transactionId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2830">
                Transaction ID <span className="text-red-500" data-id="element-2831">*</span>
              </label>
              <input type="text" id="transactionId" name="transactionId" value={formData.transactionId} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" required data-id="element-2832" />
              {formErrors.transactionId && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-2833">
                  {formErrors.transactionId}
                </p>}
            </div>
            <div data-id="element-2834">
              <label htmlFor="transactionDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2835">
                Transaction Date <span className="text-red-500" data-id="element-2836">*</span>
              </label>
              <div className="relative rounded-md shadow-sm" data-id="element-2837">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2838">
                  <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-2839" />
                </div>
                <input type="date" id="transactionDate" name="transactionDate" value={formData.transactionDate} onChange={handleInputChange} className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" required data-id="element-2840" />
              </div>
              {formErrors.transactionDate && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-2841">
                  {formErrors.transactionDate}
                </p>}
            </div>
            <div data-id="element-2842">
              <label htmlFor="transactionAmount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2843">
                Transaction Amount <span className="text-red-500" data-id="element-2844">*</span>
              </label>
              <div className="relative rounded-md shadow-sm" data-id="element-2845">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2846">
                  <DollarSignIcon className="h-5 w-5 text-gray-400" data-id="element-2847" />
                </div>
                <input type="number" id="transactionAmount" name="transactionAmount" value={formData.transactionAmount} onChange={handleInputChange} step="0.01" min="0" className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" required data-id="element-2848" />
              </div>
              {formErrors.transactionAmount && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-2849">
                  {formErrors.transactionAmount}
                </p>}
            </div>
            <div data-id="element-2850">
              <label htmlFor="currency" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2851">
                Currency
              </label>
              <select id="currency" name="currency" value={formData.currency} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" data-id="element-2852">
                <option value="PKR" data-id="element-2853">PKR - Pakistani Rupee</option>
                <option value="USD" data-id="element-2854">USD - US Dollar</option>
                <option value="EUR" data-id="element-2855">EUR - Euro</option>
                <option value="GBP" data-id="element-2856">GBP - British Pound</option>
              </select>
            </div>
          </div>
        </Card>

        {/* Dispute Details */}
        <Card data-id="element-2857">
          <h2 className="text-lg font-medium mb-4 flex items-center" data-id="element-2858">
            <AlertTriangleIcon className="h-5 w-5 mr-2 text-blue-500" data-id="element-2859" />
            Dispute Details
          </h2>
          <div className="space-y-4" data-id="element-2860">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2861">
              <div data-id="element-2862">
                <label htmlFor="disputeType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2863">
                  Dispute Type <span className="text-red-500" data-id="element-2864">*</span>
                </label>
                <select id="disputeType" name="disputeType" value={formData.disputeType} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" required data-id="element-2865">
                  <option value="" data-id="element-2866">Select Dispute Type</option>
                  <option value="chargeback" data-id="element-2867">Chargeback</option>
                  <option value="refund" data-id="element-2868">Refund</option>
                  <option value="fraud" data-id="element-2869">Fraud</option>
                </select>
                {formErrors.disputeType && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-2870">
                    {formErrors.disputeType}
                  </p>}
              </div>
              <div data-id="element-2871">
                <label htmlFor="amountClaimed" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2872">
                  Amount Claimed <span className="text-red-500" data-id="element-2873">*</span>
                </label>
                <div className="relative rounded-md shadow-sm" data-id="element-2874">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2875">
                    <DollarSignIcon className="h-5 w-5 text-gray-400" data-id="element-2876" />
                  </div>
                  <input type="number" id="amountClaimed" name="amountClaimed" value={formData.amountClaimed} onChange={handleInputChange} step="0.01" min="0" className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" required data-id="element-2877" />
                </div>
                {formErrors.amountClaimed && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-2878">
                    {formErrors.amountClaimed}
                  </p>}
              </div>
            </div>
            <div data-id="element-2879">
              <label htmlFor="reason" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2880">
                Reason for Dispute <span className="text-red-500" data-id="element-2881">*</span>
              </label>
              <textarea id="reason" name="reason" value={formData.reason} onChange={handleInputChange} rows={3} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" required data-id="element-2882" />
              {formErrors.reason && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-2883">
                  {formErrors.reason}
                </p>}
            </div>
          </div>
        </Card>

        {/* Customer Information */}
        <Card data-id="element-2884">
          <h2 className="text-lg font-medium mb-4 flex items-center" data-id="element-2885">
            <UserIcon className="h-5 w-5 mr-2 text-blue-500" data-id="element-2886" />
            Customer Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2887">
            <div data-id="element-2888">
              <label htmlFor="customerName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2889">
                Customer Name <span className="text-red-500" data-id="element-2890">*</span>
              </label>
              <input type="text" id="customerName" name="customerName" value={formData.customerName} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" required data-id="element-2891" />
              {formErrors.customerName && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-2892">
                  {formErrors.customerName}
                </p>}
            </div>
            <div data-id="element-2893">
              <label htmlFor="customerContact" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2894">
                Customer Contact (Email/Phone){' '}
                <span className="text-red-500" data-id="element-2895">*</span>
              </label>
              <input type="text" id="customerContact" name="customerContact" value={formData.customerContact} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" required data-id="element-2896" />
              {formErrors.customerContact && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-2897">
                  {formErrors.customerContact}
                </p>}
            </div>
          </div>
        </Card>

        {/* Supporting Documents */}
        <Card data-id="element-2898">
          <h2 className="text-lg font-medium mb-4 flex items-center" data-id="element-2899">
            <FileTextIcon className="h-5 w-5 mr-2 text-blue-500" data-id="element-2900" />
            Supporting Documents
          </h2>
          <div className="space-y-4" data-id="element-2901">
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md p-6 text-center" data-id="element-2902">
              <input type="file" ref={fileInputRef} onChange={handleFileUpload} multiple accept=".pdf,.jpg,.jpeg,.png,.docx" className="hidden" id="file-upload" data-id="element-2903" />
              <div className="space-y-2" data-id="element-2904">
                <div className="flex justify-center" data-id="element-2905">
                  <UploadIcon className="h-12 w-12 text-gray-400" data-id="element-2906" />
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400" data-id="element-2907">
                  <label htmlFor="file-upload" className="relative cursor-pointer rounded-md font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300" data-id="element-2908">
                    <span data-id="element-2909">Upload files</span>
                  </label>{' '}
                  or drag and drop
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2910">
                  PDF, JPG, PNG, DOCX up to 5MB
                </p>
                <p className="text-xs text-amber-500 dark:text-amber-400" data-id="element-2911">
                  Note: You can continue even if file upload has issues
                </p>
              </div>
            </div>
            {uploadError && <div className="text-sm bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800 rounded p-3 text-yellow-700 dark:text-yellow-400" data-id="element-2912">
                <div className="flex items-start" data-id="element-2913">
                  <InfoIcon className="h-5 w-5 mr-2 flex-shrink-0" data-id="element-2914" />
                  <div data-id="element-2915">
                    <p data-id="element-2916">{uploadError}</p>
                    <p className="mt-1 font-medium" data-id="element-2917">
                      You can continue with form submission.
                    </p>
                  </div>
                </div>
              </div>}
            {uploadedDocuments.length > 0 && <div className="mt-4" data-id="element-2918">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2919">
                  Uploaded Documents ({uploadedDocuments.length})
                </h3>
                <ul className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-2920">
                  {uploadedDocuments.map(doc => <li key={doc.id} className="py-3 flex justify-between items-center" data-id="element-2921">
                      <div className="flex items-center" data-id="element-2922">
                        <span className="text-xl mr-2" data-id="element-2923">
                          {getFileIcon(doc.type)}
                        </span>
                        <div data-id="element-2924">
                          <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-2925">
                            {doc.name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2926">
                            {formatFileSize(doc.size)} • Uploaded{' '}
                            {new Date(doc.uploadDate).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <button type="button" onClick={() => removeDocument(doc.id)} className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300" data-id="element-2927">
                        <XCircleIcon className="h-5 w-5" data-id="element-2928" />
                      </button>
                    </li>)}
                </ul>
              </div>}
          </div>
        </Card>

        {/* Compliance & Verification */}
        <Card data-id="element-2929">
          <h2 className="text-lg font-medium mb-4 flex items-center" data-id="element-2930">
            <ShieldIcon className="h-5 w-5 mr-2 text-blue-500" data-id="element-2931" />
            Compliance & Verification
          </h2>
          <div className="space-y-4" data-id="element-2932">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2933">
              <div className="flex items-center" data-id="element-2934">
                <input type="checkbox" id="customerVerified" name="customerVerified" checked={formData.customerVerified} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-2935" />
                <label htmlFor="customerVerified" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-2936">
                  Customer Identity Verified
                </label>
              </div>
              <div className="flex items-center" data-id="element-2937">
                <input type="checkbox" id="merchantVerified" name="merchantVerified" checked={formData.merchantVerified} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-2938" />
                <label htmlFor="merchantVerified" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-2939">
                  Merchant Account Verified
                </label>
              </div>
              <div className="flex items-center" data-id="element-2940">
                <input type="checkbox" id="amlChecked" name="amlChecked" checked={formData.amlChecked} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-2941" />
                <label htmlFor="amlChecked" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-2942">
                  AML Check Completed
                </label>
              </div>
              <div className="flex items-center" data-id="element-2943">
                <input type="checkbox" id="kycChecked" name="kycChecked" checked={formData.kycChecked} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-2944" />
                <label htmlFor="kycChecked" className="ml-2 block text-sm text-gray-700 dark:text-gray-300" data-id="element-2945">
                  KYC Verification Completed
                </label>
              </div>
            </div>
            <div data-id="element-2946">
              <label htmlFor="additionalNotes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2947">
                Additional Notes
              </label>
              <textarea id="additionalNotes" name="additionalNotes" value={formData.additionalNotes} onChange={handleInputChange} rows={2} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-800" data-id="element-2948" />
            </div>
          </div>
        </Card>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3" data-id="element-2949">
          <button type="button" onClick={goToDisputeList} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-2950">
            Cancel
          </button>
          <button type="submit" disabled={isSubmitting} className={`px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white ${isSubmitting ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`} data-id="element-2951">
            {isSubmitting ? <>
                <span className="inline-block animate-spin mr-2" data-id="element-2952">⏳</span>
                Submitting...
              </> : 'Submit Dispute'}
          </button>
        </div>
      </form>

      {/* Success Modal */}
      {showSuccessModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-2953">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full" data-id="element-2954">
            <div className="text-center" data-id="element-2955">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/30" data-id="element-2956">
                <CheckCircleIcon className="h-8 w-8 text-green-600 dark:text-green-400" data-id="element-2957" />
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white" data-id="element-2958">
                Dispute Submitted Successfully
              </h3>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400" data-id="element-2959">
                Your dispute has been successfully submitted. The dispute ID is{' '}
                <span className="font-bold" data-id="element-2960">{disputeId}</span>.
              </p>
              <div className="mt-6 flex justify-center space-x-3" data-id="element-2961">
                <button onClick={goToDisputeList} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700" data-id="element-2962">
                  Go to Dispute List
                </button>
                <button onClick={viewDispute} className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700" data-id="element-2963">
                  View Dispute
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
};
export default DisputeForm;